import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2, ArrowRight, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const T = {
  fr: {
    eyebrow: "La Curiosité", title: "L'Étincelle du Cocoon Mind",
    placeholder: "Qu'est-ce qui t'intrigue ?", spark: "Allumer l'étincelle",
    loading: "L'Élan s'émerveille…", cta: "Continuer ce fil dans le Cocoon", again: "Une autre étincelle",
    error: "L'étincelle n'a pas jailli. Réessaie.", hint: "Un mot, une question, une envie — L'Élan te répond en direct.",
    live: "En direct · L'Élan t'écoute", orbHint: "Choisis une étincelle — ou écris ton propre sujet.",
    topics: ["les trous noirs", "pourquoi on rêve", "la Renaissance", "les océans", "la mémoire", "le hasard", "les fourmis", "l'infini", "le sommeil", "les couleurs"],
  },
  en: {
    eyebrow: "Curiosity", title: "The Spark of Cocoon Mind",
    placeholder: "What are you curious about?", spark: "Light the spark",
    loading: "L'Élan is marvelling…", cta: "Continue this thread in the Cocoon", again: "Another spark",
    error: "The spark didn't catch. Try again.", hint: "A word, a question, a whim — L'Élan replies live.",
    live: "Live · L'Élan is listening", orbHint: "Pick a spark — or type your own topic.",
    topics: ["black holes", "why we dream", "the Renaissance", "the oceans", "memory", "chance", "ants", "infinity", "sleep", "colours"],
  },
  es: {
    eyebrow: "La Curiosidad", title: "La Chispa de Cocoon Mind",
    placeholder: "¿Qué te intriga?", spark: "Encender la chispa",
    loading: "L'Élan se maravilla…", cta: "Seguir este hilo en el Cocoon", again: "Otra chispa",
    error: "La chispa no prendió. Inténtalo de nuevo.", hint: "Una palabra, una pregunta, un capricho — L'Élan responde al instante.",
    live: "En directo · L'Élan te escucha", orbHint: "Elige una chispa — o escribe tu propio tema.",
    topics: ["los agujeros negros", "por qué soñamos", "el Renacimiento", "los océanos", "la memoria", "el azar", "las hormigas", "el infinito", "el sueño", "los colores"],
  },
  nl: {
    eyebrow: "De Nieuwsgierigheid", title: "De Vonk van Cocoon Mind",
    placeholder: "Waar ben je nieuwsgierig naar?", spark: "Ontsteek de vonk",
    loading: "L'Élan verwondert zich…", cta: "Zet deze draad voort in de Cocoon", again: "Nog een vonk",
    error: "De vonk sloeg niet aan. Probeer opnieuw.", hint: "Een woord, een vraag, een gril — L'Élan antwoordt live.",
    live: "Live · L'Élan luistert", orbHint: "Kies een vonk — of typ je eigen onderwerp.",
    topics: ["zwarte gaten", "waarom we dromen", "de Renaissance", "de oceanen", "het geheugen", "toeval", "mieren", "oneindigheid", "slaap", "kleuren"],
  },
};

// Puces de sujets statiques (remplace l'ancien radar animé « SparkEcho »).

export function HeroSpark() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [spark, setSpark] = useState("");

  const ignite = async (override) => {
    const q = (override ?? topic).trim();
    if (!q || loading) return;
    setTopic(q);
    setLoading(true); setSpark("");
    try {
      const { data } = await api.get("/public/spark", { params: { topic: q, lang } });
      setSpark(data.spark);
      try { localStorage.setItem("coccon_pending_prompt", q); } catch { /* noop */ }
    } catch (e) {
      setSpark("");
      import("sonner").then(({ toast }) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || c.error));
    } finally { setLoading(false); }
  };

  const reset = () => { setSpark(""); setTopic(""); };

  return (
    <section className="relative px-6 mt-8 sm:mt-10 mb-4" data-testid="hero-spark">
      <div className="max-w-6xl mx-auto relative overflow-hidden rounded-[2rem] border border-primary/25 glass frame-pulse">
          <div className="relative z-10 px-6 sm:px-12 py-12 sm:py-14 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-6" data-testid="hero-spark-eyebrow">
              <Sparkles size={13} /> {c.eyebrow}
            </span>
            <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-4" data-testid="hero-spark-title">{c.title}</h2>
            <p className="text-foreground/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed italic mb-8">{c.hint}</p>

            <div className="w-full max-w-xl mx-auto">
              <AnimatePresence mode="wait">
                {!spark && !loading ? (
                  <motion.div key="input" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <p className="text-foreground/55 text-xs sm:text-sm mb-4">{c.orbHint}</p>
                    <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="hero-spark-topics">
                      {c.topics.map((tp, i) => (
                        <button key={i} type="button" onClick={() => ignite(tp)} data-testid={`hero-spark-topic-${i}`}
                          className="text-xs sm:text-sm px-3.5 py-2 rounded-full border border-primary/25 text-foreground/70 hover:text-primary hover:border-primary/50 hover:bg-primary/10 transition-colors">
                          {tp}
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-center mb-4">
                      <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass border border-primary/25 text-primary text-[11px] uppercase tracking-[0.2em]">
                        <span className="spark-dot h-2 w-2 rounded-full bg-primary shadow-[0_0_10px_hsl(28_96%_56%)]" />
                        {c.live}
                      </span>
                    </div>
                    <div className="spark-frame">
                      <div className="spark-inner bg-[hsl(215_32%_8%/0.82)] backdrop-blur-xl p-2 flex flex-col sm:flex-row gap-2">
                        <input
                          value={topic}
                          onChange={(e) => setTopic(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && ignite()}
                          placeholder={c.placeholder}
                          data-testid="hero-spark-input"
                          className="flex-1 min-w-0 bg-transparent px-4 py-3.5 text-sm sm:text-base text-foreground placeholder:text-foreground/45 focus:outline-none"
                        />
                        <button
                          onClick={() => ignite()}
                          disabled={loading || !topic.trim()}
                          data-testid="hero-spark-btn"
                          className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm whitespace-nowrap disabled:opacity-50"
                        >
                          <Sparkles size={16} /> {c.spark}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ) : loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="flex flex-col items-center gap-4 py-10" data-testid="hero-spark-loading">
                    <motion.div className="relative" style={{ width: 72, height: 72 }}>
                      {[0, 1, 2].map((k) => (
                        <motion.span key={k} className="absolute inset-0 rounded-full border border-primary/40"
                          animate={{ scale: [1, 1.6], opacity: [0.7, 0] }} transition={{ duration: 1.6, repeat: Infinity, delay: k * 0.5 }} />
                      ))}
                      <Loader2 size={30} className="absolute inset-0 m-auto animate-spin text-primary" />
                    </motion.div>
                    <p className="font-display italic text-primary/85 text-lg">{c.loading}</p>
                  </motion.div>
                ) : (
                  <motion.div key="spark" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}
                    className="relative glass rounded-2xl border border-primary/30 p-6 text-left overflow-hidden" data-testid="hero-spark-result"
                    style={{ boxShadow: "0 0 60px -20px hsl(28 96% 56% / 0.7)" }}>
                    <motion.div aria-hidden className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-primary/25 blur-[70px]"
                      initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.9 }} />
                    <span className="relative inline-flex items-center gap-2 text-primary text-[11px] uppercase tracking-[0.22em] mb-3">
                      <Sparkles size={13} /> {topic}
                    </span>
                    <p className="relative text-foreground/85 leading-relaxed text-sm sm:text-base mb-5">{spark}</p>
                    <div className="relative flex flex-col sm:flex-row items-center gap-3">
                      <Link to="/entrer" data-testid="hero-spark-cta"
                        className="btn-frost-orange group inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm w-full sm:w-auto justify-center">
                        {c.cta}
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                      <button onClick={reset} data-testid="hero-spark-again"
                        className="btn-glass-soft inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/70 hover:text-primary w-full sm:w-auto justify-center">
                        <RotateCcw size={14} /> {c.again}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
      </div>
    </section>
  );
}
